import type { FeatureConfig } from '~/lib/builder-types';
import type { TechSpecResolver } from './types';

export const messagingTechSpec: TechSpecResolver = (
  stack,
  featureConfig?: FeatureConfig,
) => {
  const isWS =
    stack.realtime === 'websockets' || stack.realtime === 'supabase-realtime';
  const isSSE = stack.realtime === 'sse';
  const isPusher = stack.realtime === 'pusher-ably';
  const isMongo = stack.database === 'mongodb';

  const conversationTypes = (featureConfig?.options['conversation-type'] as
    | string[]
    | undefined) ?? ['direct', 'channels'];
  const hasChannels = conversationTypes.includes('channels');
  const hasThreads = conversationTypes.includes('threads');
  const persistence =
    (featureConfig?.options['persistence'] as string | undefined) ?? 'limited';
  const isEphemeral = persistence === 'ephemeral';

  return {
    pages: [
      ...(hasChannels
        ? [
            {
              id: 'channels-list',
              path: '/channels',
              label: 'Channels',
              description: 'Browse and join public channels',
              authRequired: true,
            },
            {
              id: 'channel-view',
              path: '/channels/:channelId',
              label: 'Channel view',
              description: 'Read and send messages in a channel',
              authRequired: true,
            },
          ]
        : []),
      {
        id: 'dm-view',
        path: '/messages/:userId',
        label: 'Direct message',
        description: '1:1 conversation with another user',
        authRequired: true,
      },
      {
        id: 'inbox',
        path: '/messages',
        label: 'Inbox',
        description: 'List of all DM conversations',
        authRequired: true,
      },
    ],
    uxFlows: [
      {
        id: 'send-message',
        title: 'User sends a message',
        steps: isWS
          ? [
              'User types in composer and submits',
              'POST /api/messages persists to DB',
              'Server emits message event over WebSocket to channel/room members',
              'All connected clients append the new message optimistically',
            ]
          : isSSE
            ? [
                'User types in composer and submits',
                'POST /api/messages persists to DB',
                'Server emits event on the SSE stream for the channel',
                'Listening clients receive the event and append the message',
              ]
            : isPusher
              ? [
                  'User types in composer and submits',
                  'POST /api/messages persists to DB',
                  'Server triggers a Pusher/Ably event on the channel',
                  'Clients subscribed to the channel receive and render the message',
                ]
              : [
                  'User types in composer and submits',
                  'POST /api/messages persists to DB',
                  'Client polls GET /api/messages every 2–5 seconds',
                  'New messages appear within the next poll window',
                ],
      },
      ...(hasChannels
        ? [
            {
              id: 'join-channel',
              title: 'User joins a channel',
              steps: [
                'User browses /channels and clicks a public channel',
                'POST /api/channels/:id/members adds the user as a member',
                'User is redirected to the channel view',
                'Membership is reflected in sidebar nav on next load',
              ],
            },
          ]
        : []),
    ],
    endpoints: [
      {
        id: 'get-messages',
        method: 'GET',
        path: '/api/messages',
        description: 'Paginated message history for a DM or channel',
        response: '{ messages: Message[], nextCursor?: string }',
      },
      {
        id: 'post-message',
        method: 'POST',
        path: '/api/messages',
        description: 'Send a new message',
        requestBody: '{ channelId?, recipientId?, content, attachments? }',
        response: '{ message: Message }',
      },
      ...(hasChannels
        ? [
            {
              id: 'get-channels',
              method: 'GET' as const,
              path: '/api/channels',
              description: 'List channels the current user is a member of',
              response: '{ channels: Channel[] }',
            },
            {
              id: 'join-channel',
              method: 'POST' as const,
              path: '/api/channels/:id/members',
              description: 'Join a public channel',
              response: '{ ok: true }',
            },
          ]
        : []),
      ...(isWS
        ? [
            {
              id: 'ws',
              method: 'GET' as const,
              path: '/ws',
              description: 'WebSocket upgrade — subscribe to a channel/DM room',
            },
          ]
        : []),
      ...(isSSE
        ? [
            {
              id: 'sse-stream',
              method: 'GET' as const,
              path: '/api/messages/stream',
              description: 'SSE stream — server pushes new message events',
            },
          ]
        : []),
    ],
    dbTables: isEphemeral
      ? []
      : isMongo
        ? [
            {
              id: 'messages',
              name: 'messages',
              columns: [
                { name: '_id', type: 'ObjectId', nullable: false },
                {
                  name: 'channelId',
                  type: 'ObjectId ref channels',
                  nullable: true,
                },
                {
                  name: 'recipientId',
                  type: 'ObjectId ref users',
                  nullable: true,
                },
                {
                  name: 'senderId',
                  type: 'ObjectId ref users',
                  nullable: false,
                },
                { name: 'content', type: 'string', nullable: false },
                { name: 'createdAt', type: 'Date', nullable: false },
                ...(hasThreads
                  ? [
                      {
                        name: 'parentId',
                        type: 'ObjectId ref messages',
                        nullable: true,
                      },
                    ]
                  : []),
              ],
            },
            ...(hasChannels
              ? [
                  {
                    id: 'channels',
                    name: 'channels',
                    columns: [
                      { name: '_id', type: 'ObjectId', nullable: false },
                      { name: 'name', type: 'string', nullable: false },
                      { name: 'isPrivate', type: 'boolean', nullable: false },
                      { name: 'createdAt', type: 'Date', nullable: false },
                    ],
                  },
                  {
                    id: 'channel_members',
                    name: 'channel_members',
                    columns: [
                      { name: '_id', type: 'ObjectId', nullable: false },
                      {
                        name: 'channelId',
                        type: 'ObjectId ref channels',
                        nullable: false,
                      },
                      {
                        name: 'userId',
                        type: 'ObjectId ref users',
                        nullable: false,
                      },
                      { name: 'joinedAt', type: 'Date', nullable: false },
                    ],
                  },
                ]
              : []),
          ]
        : [
            {
              id: 'messages',
              name: 'messages',
              columns: [
                { name: 'id', type: 'uuid', nullable: false },
                {
                  name: 'channel_id',
                  type: 'uuid FK channels',
                  nullable: true,
                },
                { name: 'recipient_id', type: 'uuid FK users', nullable: true },
                { name: 'sender_id', type: 'uuid FK users', nullable: false },
                { name: 'content', type: 'text', nullable: false },
                { name: 'created_at', type: 'timestamp', nullable: false },
                ...(hasThreads
                  ? [
                      {
                        name: 'parent_id',
                        type: 'uuid FK messages',
                        nullable: true,
                      },
                    ]
                  : []),
              ],
              notes: 'Either channel_id or recipient_id must be set (not both)',
            },
            ...(hasChannels
              ? [
                  {
                    id: 'channels',
                    name: 'channels',
                    columns: [
                      { name: 'id', type: 'uuid', nullable: false },
                      { name: 'name', type: 'text', nullable: false },
                      { name: 'is_private', type: 'boolean', nullable: false },
                      {
                        name: 'created_at',
                        type: 'timestamp',
                        nullable: false,
                      },
                    ],
                  },
                  {
                    id: 'channel_members',
                    name: 'channel_members',
                    columns: [
                      {
                        name: 'channel_id',
                        type: 'uuid FK channels',
                        nullable: false,
                      },
                      {
                        name: 'user_id',
                        type: 'uuid FK users',
                        nullable: false,
                      },
                      { name: 'joined_at', type: 'timestamp', nullable: false },
                    ],
                    notes: 'Composite PK (channel_id, user_id)',
                  },
                ]
              : []),
          ],
  };
};
